import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

export interface ThreadMessage {
  id: string;
  thread_id: string;
  sender_id: string;
  content: string;
  read_at: string | null;
  created_at: string;
  sender?: {
    id: string;
    full_name: string;
    avatar_url: string | null;
  };
}

export interface MessageThread {
  id: string;
  participant_1_id: string;
  participant_2_id: string;
  created_at: string;
  updated_at: string;
  thread_type: string;
  mentorship_request_id: string | null;
  otherParticipant: {
    id: string;
    full_name: string;
    avatar_url: string | null;
  };
  lastMessage?: {
    content: string;
    created_at: string;
    sender_id: string;
  } | null;
  unreadCount: number;
}

export function useMessageThreads() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const threadsQuery = useQuery({
    queryKey: ["message-threads", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      console.log("Fetching threads for user:", user.id);
      
      const { data: threads, error } = await supabase
        .from("message_threads")
        .select(`
          id,
          participant_1_id,
          participant_2_id,
          created_at,
          updated_at,
          thread_type,
          mentorship_request_id,
          participant_1:profiles!message_threads_participant_1_id_fkey(id, full_name, avatar_url),
          participant_2:profiles!message_threads_participant_2_id_fkey(id, full_name, avatar_url)
        `)
        .or(`participant_1_id.eq.${user.id},participant_2_id.eq.${user.id}`)
        .order("updated_at", { ascending: false });

      if (error) {
        console.error("Error fetching threads:", error);
        throw error;
      }
      
      console.log("Threads fetched:", threads);

      // Get last message and unread count for each thread
      const threadsWithMessages = await Promise.all(
        (threads || []).map(async (thread) => {
          const { data: messages } = await supabase
            .from("messages")
            .select("content, created_at, sender_id")
            .eq("thread_id", thread.id)
            .order("created_at", { ascending: false })
            .limit(1);

          const { count } = await supabase
            .from("messages")
            .select("*", { count: "exact", head: true })
            .eq("thread_id", thread.id)
            .neq("sender_id", user.id)
            .is("read_at", null);

          const isParticipant1 = thread.participant_1_id === user.id;
          const otherParticipant = isParticipant1 
            ? (Array.isArray(thread.participant_2) ? thread.participant_2[0] : thread.participant_2)
            : (Array.isArray(thread.participant_1) ? thread.participant_1[0] : thread.participant_1);

          return {
            id: thread.id,
            participant_1_id: thread.participant_1_id,
            participant_2_id: thread.participant_2_id,
            created_at: thread.created_at,
            updated_at: thread.updated_at,
            thread_type: (thread as any).thread_type || 'general',
            mentorship_request_id: (thread as any).mentorship_request_id || null,
            otherParticipant: otherParticipant || { id: "", full_name: "Unknown", avatar_url: null },
            lastMessage: messages?.[0] || null,
            unreadCount: count || 0,
          } as MessageThread;
        })
      );

      // Sort by last message date
      return threadsWithMessages.sort((a, b) => {
        const aDate = a.lastMessage?.created_at || a.updated_at;
        const bDate = b.lastMessage?.created_at || b.updated_at;
        return new Date(bDate).getTime() - new Date(aDate).getTime();
      });
    },
    enabled: !!user?.id,
  });

  // Subscribe to real-time updates
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel("message-threads-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "message_threads",
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["message-threads", user.id] });
        }
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "messages",
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["message-threads", user.id] });
          queryClient.invalidateQueries({ queryKey: ["thread-unread-count", user.id] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, queryClient]);

  const deleteThread = useMutation({
    mutationFn: async (threadId: string) => {
      // Delete all messages in the thread first
      const { error: messagesError } = await supabase
        .from("messages")
        .delete()
        .eq("thread_id", threadId);

      if (messagesError) throw messagesError;

      // Then delete the thread
      const { error } = await supabase
        .from("message_threads")
        .delete()
        .eq("id", threadId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["message-threads"] });
      queryClient.invalidateQueries({ queryKey: ["thread-unread-count"] });
    },
  });

  return {
    threads: threadsQuery.data || [],
    isLoading: threadsQuery.isLoading,
    deleteThread,
  };
}

export function useThreadMessages(threadId?: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const messagesQuery = useQuery({
    queryKey: ["thread-messages", threadId],
    queryFn: async () => {
      if (!threadId) return [];

      const { data, error } = await supabase
        .from("messages")
        .select(`
          id,
          thread_id,
          sender_id,
          content,
          read_at,
          created_at,
          sender:profiles!messages_sender_id_fkey(id, full_name, avatar_url)
        `)
        .eq("thread_id", threadId)
        .order("created_at", { ascending: true });

      if (error) throw error;
      return data as ThreadMessage[];
    },
    enabled: !!threadId && !!user?.id,
  });

  // Subscribe to real-time updates for this thread's messages
  useEffect(() => {
    if (!threadId || !user?.id) return;

    const channel = supabase
      .channel(`thread-messages-${threadId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "messages",
          filter: `thread_id=eq.${threadId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["thread-messages", threadId] });
          queryClient.invalidateQueries({ queryKey: ["thread-unread-count"] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [threadId, user?.id, queryClient]);

  const sendMessage = useMutation({
    mutationFn: async ({ threadId, content }: { threadId: string; content: string }) => {
      if (!user?.id) throw new Error("Not authenticated");

      const { data: message, error } = await supabase
        .from("messages")
        .insert({
          thread_id: threadId,
          sender_id: user.id,
          content,
        })
        .select()
        .single();

      if (error) throw error;

      // Update thread's updated_at
      await supabase
        .from("message_threads")
        .update({ updated_at: new Date().toISOString() })
        .eq("id", threadId);

      // Trigger email notification
      try {
        await supabase.functions.invoke("notify-new-message", {
          body: { messageId: message.id, threadId },
        });
      } catch (e) {
        console.error("Failed to send email notification:", e);
      }

      return message;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["thread-messages", threadId] });
      queryClient.invalidateQueries({ queryKey: ["message-threads"] });
    },
  });

  const markAsRead = useMutation({
    mutationFn: async (messageIds: string[]) => {
      const { error } = await supabase
        .from("messages")
        .update({ read_at: new Date().toISOString() })
        .in("id", messageIds);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["thread-messages", threadId] });
      queryClient.invalidateQueries({ queryKey: ["thread-unread-count"] });
    },
  });

  const editMessage = useMutation({
    mutationFn: async ({ messageId, content }: { messageId: string; content: string }) => {
      const { error } = await supabase
        .from("messages")
        .update({ content })
        .eq("id", messageId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["thread-messages", threadId] });
    },
  });

  const deleteMessage = useMutation({
    mutationFn: async (messageId: string) => {
      const { error } = await supabase
        .from("messages")
        .delete()
        .eq("id", messageId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["thread-messages", threadId] });
      queryClient.invalidateQueries({ queryKey: ["thread-unread-count"] });
    },
  });

  return {
    messages: messagesQuery.data || [],
    isLoading: messagesQuery.isLoading,
    sendMessage,
    markAsRead,
    editMessage,
    deleteMessage,
  };
}

export function useThreadUnreadCount() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["thread-unread-count", user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;

      // Get all threads where user is involved
      const { data: threads, error: threadError } = await supabase
        .from("message_threads")
        .select("id")
        .or(`participant_1_id.eq.${user.id},participant_2_id.eq.${user.id}`);

      if (threadError) throw threadError;
      if (!threads || threads.length === 0) return 0;

      const threadIds = threads.map((t) => t.id);

      // Count unread messages where user is NOT the sender
      const { count, error } = await supabase
        .from("messages")
        .select("*", { count: "exact", head: true })
        .in("thread_id", threadIds)
        .neq("sender_id", user.id)
        .is("read_at", null);

      if (error) throw error;
      return count || 0;
    },
    enabled: !!user?.id,
    refetchInterval: 30000,
  });
}

export function useGetOrCreateThread() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (otherUserId: string) => {
      if (!user?.id) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .rpc("get_or_create_thread", {
          user_a: user.id,
          user_b: otherUserId,
        });

      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["message-threads"] });
    },
  });
}
